import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Stack,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import VotingSidebar from "../components/VotingSidebar";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

// Types
interface PortfolioPosition {
  id: number;
  symbol: string;
  name: string;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  marketValue: number;
  totalCost: number;
  gainLoss: number;
  gainLossPercent: number;
  sector: string;
  lastUpdated: string;
  img: string;
  isDisabled: boolean;
}

type TimePoint = {
  x: string;
  y: number;
};

type TimePeriod = "1D" | "1W" | "1M" | "3M" | "6M" | "1Y" | "YTD" | "All";

// Mock data for different time periods
const timePeriodsData: Record<TimePeriod, TimePoint[]> = {
    "1D": [
      { x: "9:30", y: 18750.25 },
      { x: "10:30", y: 18825.50 },
      { x: "11:30", y: 18950.75 },
      { x: "12:30", y: 18875.25 },
      { x: "13:30", y: 18990.50 },
      { x: "14:30", y: 18895.75 },
      { x: "15:30", y: 18924.43 },
    ],
    "1W": [
      { x: "Mon", y: 18450.25 },
      { x: "Tue", y: 18575.50 },
      { x: "Wed", y: 18725.75 },
      { x: "Thu", y: 18850.25 },
      { x: "Fri", y: 18924.43 },
    ],
    "1M": [
      { x: "Week 1", y: 17950.25 },
      { x: "Week 2", y: 18250.50 },
      { x: "Week 3", y: 18650.75 },
      { x: "Week 4", y: 18924.43 },
    ],
    "3M": [
      { x: "Jul", y: 16950.25 },
      { x: "Aug", y: 17850.50 },
      { x: "Sep", y: 18924.43 },
    ],
    "6M": [
      { x: "Apr", y: 15245.95 },
      { x: "May", y: 16150.25 },
      { x: "Jun", y: 16950.50 },
      { x: "Jul", y: 17550.75 },
      { x: "Aug", y: 18250.25 },
      { x: "Sep", y: 18924.43 },
    ],
    "1Y": [
      { x: "Q4 2022", y: 14250.25 },
      { x: "Q1 2023", y: 15750.50 },
      { x: "Q2 2023", y: 17250.75 },
      { x: "Q3 2023", y: 18924.43 },
    ],
    YTD: [
      { x: "Jan", y: 15250.25 },
      { x: "Mar", y: 16450.50 },
      { x: "May", y: 17250.75 },
      { x: "Jul", y: 18150.25 },
      { x: "Sep", y: 18924.43 },
    ],
    All: [
      { x: "2020", y: 12250.25 },
      { x: "2021", y: 14550.50 },
      { x: "2022", y: 16750.75 },
      { x: "2023", y: 18924.43 },
    ],
};

const Portfolio: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>("6M");
  const [openBuyDialog, setOpenBuyDialog] = useState(false);
  const [openSellDialog, setOpenSellDialog] = useState(false);
  const [votingSidebarOpen, setVotingSidebarOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] =
    useState<PortfolioPosition | null>(null);
  const [buyQuantity, setBuyQuantity] = useState("");
  const [sellQuantity, setSellQuantity] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Mock portfolio data
  const portfolioPositions: PortfolioPosition[] = [
    {
      id: 1,
      symbol: "TSLA",
      name: "Tesla Inc.",
      shares: 15.234,
      avgPrice: 180.5,
      currentPrice: 205.25,
      marketValue: 3126.78,
      totalCost: 2749.74,
      gainLoss: 377.04,
      gainLossPercent: 13.71,
      sector: "Automotive",
      lastUpdated: "2024-01-15",
      img: "/tesla_logoV1.png",
      isDisabled: false,
    },
    {
      id: 2,
      symbol: "NVDA",
      name: "Nvidia Corp.",
      shares: 8.456,
      avgPrice: 425.3,
      currentPrice: 875.28,
      marketValue: 7401.37,
      totalCost: 3596.34,
      gainLoss: 3805.03,
      gainLossPercent: 105.8,
      sector: "Technology",
      lastUpdated: "2024-01-15",
      img: "https://cdn.govotr.com/logos/NVDA.png",
      isDisabled: true,
    },
    {
      id: 3,
      symbol: "GS",
      name: "Goldman Sachs Group",
      shares: 12.789,
      avgPrice: 385.45,
      currentPrice: 362.15,
      marketValue: 4631.54,
      totalCost: 4929.32,
      gainLoss: -297.78,
      gainLossPercent: -6.04,
      sector: "Financial",
      lastUpdated: "2024-01-15",
      img: "/gs.png",
      isDisabled: false,
    },
    {
      id: 4,
      symbol: "JNJ",
      name: "Johnson & Johnson",
      shares: 25.567,
      avgPrice: 155.3,
      currentPrice: 147.25,
      marketValue: 3764.74,
      totalCost: 3970.55,
      gainLoss: -205.81,
      gainLossPercent: -5.18,
      sector: "Healthcare",
      lastUpdated: "2024-01-15",
      img: "https://cdn.govotr.com/logos/JNJ.png",
      isDisabled: true,
    },
    {
      id: 5,
      symbol: "QQQ",
      name: "Invesco QQQ Trust",
      shares: 10.5,
      avgPrice: 420.0,
      currentPrice: 478.6,
      marketValue: 5025.3,
      totalCost: 4410.0,
      gainLoss: 615.3,
      gainLossPercent: 13.95,
      sector: "ETF",
      lastUpdated: "2024-01-15",
      img: "https://cdn.govotr.com/logos/IVR.png",
      isDisabled: false,
    },
    {
      id: 6,
      symbol: "SPLV",
      name: "Invesco S&P 500 Low Volatility ETF",
      shares: 40,
      avgPrice: 71.1,
      currentPrice: 68.35,
      marketValue: 2734.0,
      totalCost: 2844.0,
      gainLoss: -110.0,
      gainLossPercent: -3.87,
      sector: "ETF",
      lastUpdated: "2024-01-15",
      img: "https://cdn.govotr.com/logos/IVR.png",
      isDisabled: true,
    },
  ];

  // Action handlers
  const handleBuy = (position: PortfolioPosition) => {
    setSelectedPosition(position);
    setBuyQuantity("");
    setOpenBuyDialog(true);
  };

  const handleSell = (position: PortfolioPosition) => {
    setSelectedPosition(position);
    setSellQuantity("");
    setOpenSellDialog(true);
  };

  const handleVote = (position: PortfolioPosition) => {
    setSelectedPosition(position);
    setVotingSidebarOpen(true);
  };

  const handleVotingSidebarClose = () => {
    setVotingSidebarOpen(false);
    setSelectedPosition(null);
  };

  // Action execution handlers
  const executeAction = (action: string, quantity?: string) => {
    if (!selectedPosition) return;

    const messages = {
      buy: `Successfully bought ${quantity} shares of ${selectedPosition.symbol}`,
      sell: `Successfully sold ${quantity} shares of ${selectedPosition.symbol}`,
      vote: `Voting interface opened for ${selectedPosition.symbol}`,
    };

    setSnackbar({
      open: true,
      message:
        messages[action as keyof typeof messages] ||
        "Action completed successfully",
      severity: "success",
    });

    // Close appropriate dialog
    if (action === "buy") setOpenBuyDialog(false);
    if (action === "sell") setOpenSellDialog(false);
    if (action === "vote") handleVotingSidebarClose();
  };

  const ActionDialog = ({
    open,
    onClose,
    title,
    children,
    actionText,
    actionColor,
    onAction,
  }: {
    open: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    actionText: string;
    actionColor: string;
    onAction: () => void;
  }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#18010a", fontWeight: 600 }}>
        {title}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onAction}
          variant="contained"
          sx={{
            bgcolor: actionColor,
            "&:hover": { bgcolor: actionColor, opacity: 0.8 },
          }}
        >
          {actionText}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        maxWidth: "1400px",
        mx: "auto",
      }}
    >
      {/* Welcome Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 600, color: "#18010a" }}
        >
          Welcome back, Andrew
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
          . Here is a quick snapshot of your Portfolio
        </Typography>
      </Box>

      {/* Portfolio Overview Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Left Card - Portfolio Value & Graph */}
        <Card
          sx={{
            bgcolor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
                               <Typography
                   variant="h5"
                   sx={{ fontWeight: 600, color: "#18010a", mb: 0.5 }}
                 >
                   $18,924.43
                 </Typography>
                 <Typography
                   variant="body2"
                   color="success"
                   sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                 >
                   <TrendingUpIcon sx={{ fontSize: 16 }} />
                   +$3,678.48 (+24.12%)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "rgba(0,0,0,0.1)",
                  color: "text.secondary",
                  minWidth: "auto",
                  px: 2,
                  py: 0.5,
                  fontSize: "0.75rem",
                }}
              >
                Report
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "rgba(0,0,0,0.1)",
                  color: "text.secondary",
                  minWidth: "auto",
                  px: 2,
                  py: 0.5,
                  fontSize: "0.75rem",
                }}
              >
                View
              </Button>
            </Box>
          </Box>

          {/* Time Period Selector */}
          <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
            {(
              ["1D", "1W", "1M", "3M", "6M", "1Y", "YTD", "All"] as TimePeriod[]
            ).map((period) => (
              <Button
                key={period}
                variant="text"
                size="small"
                onClick={() => setSelectedPeriod(period)}
                sx={{
                  color:
                    period === selectedPeriod ? "#760731" : "text.secondary",
                  fontWeight: period === selectedPeriod ? 600 : 400,
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  fontSize: "0.75rem",
                }}
              >
                {period}
              </Button>
            ))}
          </Box>

          {/* Graph */}
          <Box sx={{ height: 200, width: "100%" }}>
            <ReactApexChart
              type="area"
              height="100%"
              series={[
                {
                  name: "Portfolio Value",
                  data: timePeriodsData[selectedPeriod].map(
                    (point: TimePoint) => point.y
                  ),
                },
              ]}
              options={{
                chart: {
                  type: "area",
                  toolbar: { show: false },
                  sparkline: {
                    enabled: false,
                  },
                  animations: {
                    enabled: true,
                    speed: 800,
                    dynamicAnimation: {
                      enabled: true,
                      speed: 350,
                    },
                  },
                },
                stroke: {
                  curve: "smooth",
                  width: 2,
                  colors: ["#760731"],
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.45,
                    opacityTo: 0.05,
                    stops: [0, 100],
                  },
                },
                colors: ["#760731"],
                grid: {
                  borderColor: "#f1f1f1",
                  strokeDashArray: 3,
                  xaxis: { lines: { show: false } },
                },
                xaxis: {
                  categories: timePeriodsData[selectedPeriod].map(
                    (point: TimePoint) => point.x
                  ),
                  labels: {
                    style: {
                      colors: "#666",
                      fontSize: "12px",
                    },
                  },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: {
                    style: {
                      colors: "#666",
                      fontSize: "12px",
                    },
                    formatter: (value) => `$${value}`,
                  },
                },
                tooltip: {
                  theme: "light",
                  x: { show: false },
                  y: {
                    formatter: (value) => `$${value}`,
                    title: {
                      formatter: () => "Value:",
                    },
                  },
                  marker: { show: false },
                },
                dataLabels: {
                  enabled: false,
                },
                markers: {
                  size: 0,
                },
              }}
            />
          </Box>
        </Card>

        {/* Right Card - News */}
        <Card
          sx={{
            bgcolor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#18010a", mb: 2 }}
          >
            Top News
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Goldman Sachs Card */}
            <Box
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <img
                  src="/Goldman-Sachs.jpg"
                  alt="Goldman Sachs"
                  style={{ width: "100%", height: 100, objectFit: "cover" }}
                />
                <Box
                  component="img"
                  src="/gs.png"
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    borderRadius: "50%",
                    p: 0.5,
                    width: 24,
                    height: 24,
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "#D63B3B",
                    color: "#fff",
                    px: 1,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: "0.625rem",
                  }}
                >
                  FEATURED
                </Box>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Goldman Sachs
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Annual shareholder meeting 2025: Vote now!
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  onClick={() => handleVote(portfolioPositions[0])}
                  sx={{
                    bgcolor: "#760731",
                    "&:hover": { bgcolor: "#760731" },
                    fontSize: "0.75rem",
                    py: 0.5,
                  }}
                >
                  Vote Now
                </Button>
              </Box>
            </Box>

            {/* NVDA News */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Box
                  component="img"
                  src="https://cdn.govotr.com/logos/NVDA.png"
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                  }}
                />

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                >
                  NVDA
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Nvidia Stock Gains as AI Business Grows, What Comes Next?
              </Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Your Holdings */}
      <Card
        sx={{
          mt: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: 2,
          ":hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            transform: "translateY(-2px)",
            transition: "all 0.6s ease",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#18010a", mb: 2 }}
          >
            Your Holdings
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              bgcolor: "transparent",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 1.5,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontWeight: 500,
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                      py: 0.5,
                    }}
                  >
                    Equities
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontWeight: 500,
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                      py: 0.5,
                    }}
                  >
                    Price
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontWeight: 500,
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                      py: 0.5,
                    }}
                  >
                    Holding
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontWeight: 500,
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                      py: 0.5,
                    }}
                  >
                    Cost
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      fontWeight: 500,
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                      py: 0.5,
                      width: "220px",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {portfolioPositions.map((position) => (
                  <TableRow
                    key={position.id}
                    sx={{
                      "&:hover": { bgcolor: "rgba(0,0,0,0.01)" },
                      "& td": { borderBottom: "1px solid rgba(0,0,0,0.05)" },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          component="img"
                          src={position.img}
                          alt={position.symbol}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#18010a",
                              fontSize: "1rem",
                            }}
                          >
                            {position.symbol}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                position.gainLoss >= 0 ? "#4caf50" : "#f44336",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontSize: "0.75rem",
                            }}
                          >
                            {position.gainLoss >= 0 ? "+" : ""}
                            {position.gainLoss.toFixed(2)} (
                            {position.gainLossPercent >= 0 ? "+" : ""}
                            {position.gainLossPercent.toFixed(2)}%)
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: "#18010a",
                          fontWeight: 600,
                        }}
                      >
                        ${position.currentPrice.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: position.gainLoss >= 0 ? "#4caf50" : "#f44336",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        ${Math.abs(position.gainLoss).toFixed(2)} (
                        {position.gainLossPercent >= 0 ? "+" : ""}
                        {position.gainLossPercent.toFixed(2)}%)
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: "#18010a",
                          fontWeight: 600,
                        }}
                      >
                        ${position.marketValue.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        {position.shares.toFixed(4)} shares
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: "#18010a",
                          fontWeight: 600,
                        }}
                      >
                        ${position.totalCost.toFixed(2)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        ${(position.totalCost / position.shares).toFixed(2)}
                        /share
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>

                        {!position.isDisabled && (
                          <Box
                            component="img"
                            src="/votr-access.svg"
                            alt="VOTR Access"
                            border="1px solid rgba(0,0,0,0.05)"
                            p={0.6}
                            width={30}
                            sx={{cursor: "pointer"}}
                            height={30}
                            borderRadius={"50%"}
                          />
                        )}
                      <Box sx={{ display: "flex", gap: 1, }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleBuy(position)}
                          startIcon={<TrendingUpIcon />}
                          sx={{
                            width: "55px",
                            borderColor: "#4caf50",
                            color: "#4caf50",
                            fontSize: "0.75rem",
                            "&:hover": {
                              borderColor: "#4caf50",
                              bgcolor: "rgba(76, 175, 80, 0.04)",
                            },
                          }}
                        >
                          Buy
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSell(position)}
                          startIcon={<TrendingDownIcon />}
                          sx={{
                            width: "50px",
                            borderColor: "#f44336",
                            color: "#f44336",
                            fontSize: "0.75rem",
                            "&:hover": {
                              borderColor: "#f44336",
                              bgcolor: "rgba(244, 67, 54, 0.04)",
                            },
                          }}
                        >
                          Sell
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleVote(position)}
                          disabled={position.isDisabled}
                          endIcon={<KeyboardArrowRightIcon />}
                          sx={{
                            width: "110px",
                            bgcolor: "#760731",
                            fontSize: "0.75rem",
                            "&:hover": { bgcolor: "#760731" },
                          }}
                        >
                          Vote Now
                        </Button>
                      </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>

      {/* Buy Dialog */}
      <ActionDialog
        open={openBuyDialog}
        onClose={() => setOpenBuyDialog(false)}
        title={`Buy ${selectedPosition?.symbol} Shares`}
        actionText="Buy Shares"
        actionColor="#4caf50"
        onAction={() => executeAction("buy", buyQuantity)}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Price: ${selectedPosition?.currentPrice?.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Available Cash: $125,000
          </Typography>
        </Box>
        <TextField
          fullWidth
          label="Number of Shares"
          type="number"
          value={buyQuantity}
          onChange={(e) => setBuyQuantity(e.target.value)}
          sx={{ mb: 2 }}
        />
        {buyQuantity && (
          <Typography variant="body2" color="text.secondary">
            Total Cost: $
            {(
              parseFloat(buyQuantity) * (selectedPosition?.currentPrice || 0)
            ).toFixed(2)}
          </Typography>
        )}
      </ActionDialog>

      {/* Sell Dialog */}
      <ActionDialog
        open={openSellDialog}
        onClose={() => setOpenSellDialog(false)}
        title={`Sell ${selectedPosition?.symbol} Shares`}
        actionText="Sell Shares"
        actionColor="#f44336"
        onAction={() => executeAction("sell", sellQuantity)}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Price: ${selectedPosition?.currentPrice?.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shares Owned: {selectedPosition?.shares?.toLocaleString()}
          </Typography>
        </Box>
        <TextField
          fullWidth
          label="Number of Shares to Sell"
          type="number"
          value={sellQuantity}
          onChange={(e) => setSellQuantity(e.target.value)}
          sx={{ mb: 2 }}
        />
        {sellQuantity && (
          <Typography variant="body2" color="text.secondary">
            Total Proceeds: $
            {(
              parseFloat(sellQuantity) * (selectedPosition?.currentPrice || 0)
            ).toFixed(2)}
          </Typography>
        )}
      </ActionDialog>

      {/* Vote Dialog - Replaced with VotingSidebar */}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Voting Sidebar */}
      <VotingSidebar
        open={votingSidebarOpen}
        onClose={handleVotingSidebarClose}
        cusip={selectedPosition?.symbol || ""}
        description={selectedPosition?.name}
      />
    </Box>
  );
};

export default Portfolio;
